import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SendToBillingDialog from '../../components/shared/SendToBillingDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn, formatCurrency } from '../../lib/utils';
import { Receipt, CheckCircle2, AlertTriangle, ArrowRight, Send, Sparkles, Camera, Shield, Edit, Trash2 } from 'lucide-react';

const MOCK_CODES = {
  icd10: [
    { code: 'G44.209', description: 'Tension-type headache, unspecified', confidence: 98, status: 'verified' },
    { code: 'R03.0', description: 'Elevated blood pressure reading', confidence: 95, status: 'verified' },
  ],
  cpt: [
    { code: '99214', description: 'Office visit, established, moderate complexity', units: 1, charge: 185.00, confidence: 96, status: 'verified' },
  ],
  modifiers: []
};

const LuminaCoding = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState(MOCK_CODES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showBillingDialog, setShowBillingDialog] = useState(false);

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 95) return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" /> {confidence}%</Badge>;
    if (confidence >= 80) return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="h-3 w-3 mr-1" /> {confidence}%</Badge>;
    return <Badge className="bg-red-100 text-red-700">{confidence}%</Badge>;
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setShowBillingDialog(true);
    }, 2000);
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Sent to Billing!</h2>
            <p className="text-slate-500 mb-6">Claim has been generated and sent to your billing system</p>

            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Claim ID</p>
                  <p className="font-mono font-medium">CLM-2026-00457</p>
                </div>
                <div>
                  <p className="text-slate-500">Total Charges</p>
                  <p className="font-medium">{formatCurrency(185)}</p>
                </div>
                <div>
                  <p className="text-slate-500">ICD-10 Codes</p>
                  <p className="font-mono">{codes.icd10.map(c => c.code).join(', ')}</p>
                </div>
                <div>
                  <p className="text-slate-500">CPT Codes</p>
                  <p className="font-mono">{codes.cpt.map(c => c.code).join(', ')}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/lumina')}>
                Return to Dashboard
              </Button>
              <Button onClick={() => navigate('/lumina/new-encounter')} className="bg-emerald-600 hover:bg-emerald-700">
                Start New Encounter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="coding-billing-page">
      {/* Workflow Progress */}
      <div className="flex items-center justify-center gap-4 py-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold text-sm">✓</div>
          <span className="text-sm text-slate-500">Capture Visit</span>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400" />
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold text-sm">✓</div>
          <span className="text-sm text-slate-500">Generate SOAP Note</span>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400" />
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold text-sm">3</div>
          <span className="text-sm font-medium text-emerald-700">Review & Send to Billing</span>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Coding & Billing</h1>
          <p className="text-slate-500 mt-1">Review AI-suggested codes before sending to billing</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => navigate('/lumina/lens')}>
          <Camera className="h-4 w-4" /> Import with Lumina Lens
        </Button>
      </div>

      {/* AI Badge */}
      <Card className="bg-violet-50 border-violet-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-violet-600" />
            <div>
              <p className="font-medium text-violet-900">AI-Generated Codes</p>
              <p className="text-sm text-violet-700">Codes are automatically suggested based on your documentation and tied to medical necessity</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ICD-10 Codes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">ICD-10 Diagnosis Codes</CardTitle>
          <CardDescription>Diagnosis codes tied to documentation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {codes.icd10.map((code, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className="font-mono text-base px-3 py-1">{code.code}</Badge>
                  <div>
                    <p className="font-medium">{code.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getConfidenceBadge(code.confidence)}
                  <Button variant="ghost" size="icon"><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            <Button variant="outline" className="w-full">+ Add Diagnosis Code</Button>
          </div>
        </CardContent>
      </Card>

      {/* CPT Codes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">CPT Procedure Codes</CardTitle>
          <CardDescription>Service codes with charges</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {codes.cpt.map((code, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className="font-mono text-base px-3 py-1">{code.code}</Badge>
                  <div>
                    <p className="font-medium">{code.description}</p>
                    <p className="text-sm text-slate-500">Units: {code.units}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-semibold">{formatCurrency(code.charge)}</p>
                  {getConfidenceBadge(code.confidence)}
                  <Button variant="ghost" size="icon"><Edit className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            <Button variant="outline" className="w-full">+ Add Procedure Code</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Charges</p>
              <p className="text-2xl font-bold">{formatCurrency(codes.cpt.reduce((sum, c) => sum + c.charge, 0))}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Shield className="h-4 w-4 text-emerald-500" />
              <span>All codes verified against medical necessity</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/lumina/soap-notes')}>
          Back to SOAP Note
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          size="lg"
        >
          {isSubmitting ? (
            <>Submitting...</>
          ) : (
            <><Send className="h-5 w-5" /> Send to Billing / Generate Claim</>
          )}
        </Button>
      </div>

      <SendToBillingDialog open={showBillingDialog} onOpenChange={setShowBillingDialog} context="Coding complete. Claim generated and sent to billing." />
    </div>
  );
};

export default LuminaCoding;
